import React from "react";
import { Globe, Share2, Plus, ArrowRight, BadgePercent } from "lucide-react";
import { Button } from "../ui/button";
import { BUNDLES, BUNDLE_DISCOUNT } from "../../mock";
import { selectPackage } from "../../lib/selectPackage";

const gbp = (n) =>
  `£${n.toLocaleString("en-GB", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

const pct = Math.round(BUNDLE_DISCOUNT * 100);

export default function Bundles() {
  return (
    <section id="bundles" data-testid="bundles-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-sky-100 blur-3xl opacity-60" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="section-eyebrow">Bundles</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
            Website + social media bundles.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Get a new website and your social media managed together &mdash; and
            save over {pct}% on your website and first 4 weeks of social media.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {BUNDLES.map((b) => (
            <BundleCard key={b.name} b={b} />
          ))}
        </div>
        <p className="mt-10 text-center text-xs text-slate-500">
          Discount applies to the website and your first 4-weekly social media payment. Social media then continues on the
          normal 4-weekly rolling contract.
        </p>
      </div>
    </section>
  );
}

const Part = ({ Icon, label, name, price }) => (
  <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100">
    <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-900">{name}</div>
    </div>
    <div className="text-sm text-slate-600">{gbp(price)}</div>
  </div>
);

const BundleCard = ({ b }) => {
  const id = b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const total = b.webPrice + b.socialPrice;
  const price = Math.floor((total * (1 - BUNDLE_DISCOUNT) + 1) / 10) * 10 - 1;
  const saving = total - price;
  return (
    <div
      data-testid={`bundle-card-${id}`}
      className={`relative rounded-3xl border p-7 lg:p-8 bg-white card-lift flex flex-col ${
        b.popular ? "border-sky-300 ring-2 ring-sky-200/60 shadow-lg shadow-sky-100/50" : "border-slate-200"
      }`}
    >
      {b.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-sky-500 text-white text-[11px] font-semibold uppercase tracking-wider shadow-sm">
          Best value
        </div>
      )}
      <h3 className="text-xl font-semibold text-slate-900">{b.name}</h3>
      <p className="text-sm text-sky-700 font-medium mt-1">{b.tagline}</p>
      <div className="mt-6 space-y-2">
        <Part Icon={Globe} label="Website" name={b.web} price={b.webPrice} />
        <div className="flex justify-center"><Plus className="h-4 w-4 text-slate-400" /></div>
        <Part Icon={Share2} label="Social media · first 4 weeks" name={b.social} price={b.socialPrice} />
      </div>
      <div className="mt-6 flex items-baseline gap-2 flex-wrap" data-testid={`bundle-price-${id}`}>
        {b.from && <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">from</span>}
        <span className="text-4xl font-semibold text-slate-900">{gbp(price)}</span>
        <span className="text-sm text-slate-400 line-through">{gbp(total)}</span>
      </div>
      <div
        data-testid={`bundle-saving-${id}`}
        className="mt-3 inline-flex self-start items-center gap-1.5 px-3 py-1 rounded-full bg-sky-50 border border-sky-100 text-sky-700 text-xs font-semibold"
      >
        <BadgePercent className="h-3.5 w-3.5" /> Save {gbp(saving)}
      </div>
      <div className="flex-1" />
      <Button
        data-testid={`bundle-cta-${id}`}
        onClick={() =>
          selectPackage(
            "Website design & build",
            `Hi, I'm interested in the ${b.name} (${b.web} website + ${b.social} social media, ${b.from ? "from " : ""}${gbp(price)}).`
          )
        }
        className={`mt-6 rounded-full w-full h-11 group ${
          b.popular ? "bg-sky-600 hover:bg-sky-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"
        }`}
      >
        Choose {b.name}
        <ArrowRight className="ml-1.5 h-4 w-4 btn-arrow" />
      </Button>
    </div>
  );
};
